import { NotificationStatus } from "../../app/common/types";
import { bearer, getAccessTokenDetails, isLocalhost } from "@/app/auth/authUtils";
import { exportChatByFormat } from "@/app/services/apiEndpoints";

export enum ScopeType {
    LLMChat = "llm-chat",
    KnowledgeChat = "knowledge-chat",
    InSessionChat = "in-session-chat",
    InSessionImageChat = "in-session-image-chat",
    InternetChat = "internet-chat",
    DatabaseChat = "database-chat",
    ImageGeneration = "image-generation",
    DocumentTranslation = "document-translation",
    Presentation = "presentation",
    AccPowerpoint = "a11y-powerpoint",
    InSessionTabularChat = "in-session-tabular-chat",
}

export const getScopeValue = (currentScope: string) => {
  switch (currentScope) {
    case ScopeType.LLMChat:
      return "llm-chat";
    case ScopeType.KnowledgeChat:
      return "knowledge-base";
    case ScopeType.InternetChat:
      return "internet-search";
    case ScopeType.InSessionChat:
      return "in-session";
    case ScopeType.InSessionImageChat:
      return "in-session-image";
    case ScopeType.DatabaseChat:
      return "database";
    case ScopeType.ImageGeneration:
      return "image-generation";
    case ScopeType.DocumentTranslation:
      return "document-translation";
    case ScopeType.InSessionTabularChat:
      return "in-session-tabular-chat";
    default:
      return "";
  }
};

export const getGroupFromDepartment = (departments: any, selectedDepartment: string) => {
  const group = departments.find((item: any) => item.dpt === selectedDepartment)?.grp;
  return group;
}

export const isUserAdminForDepartment = (departments: any, selectedDepartment: string) => {
  const isAdmin = departments.filter((item: any) => item.dpt === selectedDepartment)[0]?.isAdmin;
  return isAdmin;
}

export const openNotification = (
  status: NotificationStatus,
  title: string,
  message?: string,
  notificationIndex?: number,
  addToPanel?: boolean,
  linkConfig?: {
    onClick?: () => void;
    url?: string;
  }
) => {
  return window.openNotification(status, title, message, notificationIndex, addToPanel, linkConfig)
}

export const readFileContent = async (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

export const fileTypeDictionary: { [key: string]: string } = {
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain"
};

export const allowedInSessionFileTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  "text/csv",
  'application/pdf',
  "application/msword",
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  "text/vtt",
]

export const allowedNonOcrFileTypes = [
  "text/csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "message/rfc822",
  "text/html",
  "application/vnd.ms-outlook",
  "application/pdf",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/tab-separated-values",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/vtt"
]

export const allowedKbOcrFileTypes = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif'
]

export const getAcceptedFileFormats = (types: string[]) => {
  const acceptTypes = types.map((type) => {
    if (type.startsWith('application/')) {
      const extension = type.split('/')[1];
      switch (extension) {
        case 'msword':
          return '.doc';
        case 'vnd.openxmlformats-officedocument.wordprocessingml.document':
          return '.docx';
        case 'vnd.ms-excel':
          return '.xls';
        case 'vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return '.xlsx';
        case 'pdf':
          return '.pdf';
        case 'vnd.ms-powerpoint':
          return '.ppt';
        case 'vnd.openxmlformats-officedocument.presentationml.presentation':
          return '.pptx';
        case 'vnd.ms-outlook':
          return '.msg';
        default:
          return '';
      }
    } else if (type.startsWith('image/')) {
      return `.${type.split('/')[1]}`;
    } else if (type.startsWith('text/')) {
      switch (type) {
        case 'text/plain':
          return '.txt';
        case 'text/html':
          return '.html';
        case 'text/tab-separated-values':
          return '.tsv';
        case 'text/csv':
          return '.csv';
        case 'text/vtt':
          return '.vtt'
        default:
          return '';
      }
    }
    else if (type === 'message/rfc822') {
      return '.eml';
    }
    return '';
  });
  return acceptTypes.filter(Boolean).join(',');
};

export const getFileExtension = (filename: string) => {
  const lastDotIndex = filename.lastIndexOf(".");
  const fileExtension = lastDotIndex !== -1 ? filename.substring(lastDotIndex + 1) : ""
  return fileExtension
}

export const getMimeType = (word: string): string | null => {
  return fileTypeDictionary[word] || null;
};

export const getFileNameWithoutExtension = (file: string): string => {
  const nameParts = file.split(".");
  nameParts.pop();
  return nameParts.join(".");
};

export async function consumeReadableStream(
  stream: ReadableStream<Uint8Array>,
  onStream: (chunk: string) => void,
  onDone: () => void,
  onAbort: () => void,
  isAbortedRef: React.MutableRefObject<boolean>,
): Promise<void> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()

  try {
    while (true) {
      const { done, value } = await reader.read()

      if (isAbortedRef.current) {
        onAbort()
        break
      }

      if (done) {
        onDone();
        break
      }

      if (value) {
        onStream(decoder.decode(value, { stream: true }))
      }
    }
  } catch (error) {
    console.error("Error consuming stream:", error)
  }
}

export const toBase64 = (file: File | Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);

    fileReader.onload = () => {
      if (typeof fileReader.result === "string") {
        const base64String = fileReader.result.split(",")[1] ?? "";
        resolve(base64String);
      } else {
        reject(new Error("FileReader result is not a valid string"));
      }
    };

    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

export const displayNotificationBasedOnKbStatus = (kbStatus: 'ready' | 'creating' | 'modifying' | 'not_found' | 'error'): void => {
  switch (kbStatus) {
    case "creating":
      openNotification(
        NotificationStatus.Warning,
        "Warning",
        "This knowledge base is still being created and chatting with it may produce incorrect or inconsistent results. Please wait a few minutes until it's ready!"
      );
      break;
    case "modifying":
      openNotification(
        NotificationStatus.Warning,
        "Warning",
        "This knowledge base is undergoing modification and chatting with it may produce incorrect or inconsistent results. Please wait a few minutes until it's ready!"
      );
      break;
    case "error":
      openNotification(
        NotificationStatus.Error,
        "Error",
        "Something went wrong while trying to check the status of this knowledge base."
      );
      break;
    default:
      // do nothing for other statuses
      break;
  }
};

/** Functions for processing streamed chunks */

export const processDatabaseChatChunk = (chunk: string, receivedText: string): string => {
  const isJsonChunk = chunk.trim().startsWith('{') || receivedText.includes('{"');
  
  if (isJsonChunk) {
    // The final answer for database chat is a json, and can be streamed in chunks depending on the length of the image base64 string.
    // This logic handles appending it to receivedText and overwriting the previous intermediate step.
    return chunk.trim().startsWith('{') ? chunk : receivedText + chunk;
  } else {
    return chunk; // Overwrite intermediate steps
  }
};

export const processRegularChatChunk = (chunk: string, receivedText: string): string => {
  return receivedText + chunk;
};

export const parseDatabaseChatResponse = (receivedText: string) => {
  try {
    const jsonData = JSON.parse(receivedText);
    return {
      resultText: jsonData.answer || receivedText,
      imageBase64: jsonData.image_base64 || "",
      tokenCount: jsonData.token_count,
      sourceDocuments: []
    };
  } catch (error) {
    try {
      // (For when streaming fails due to a user being on a network that blocks streaming)
      // Try to extract JSON object using regex
      const jsonMatch = receivedText.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const jsonData = JSON.parse(jsonMatch[0]);
        return {
          resultText: jsonData.answer || receivedText,
          imageBase64: jsonData.image_base64 || "",
          tokenCount: jsonData.token_count,
          sourceDocuments: []
        };
      }
    } catch (extractError) {
      console.error("Error extracting JSON from response:", extractError);
    }
    
    console.error("Error parsing DatabaseChat JSON response:", error);
    console.error("receivedText:", receivedText);
    return {
      resultText: receivedText,
      imageBase64: "",
      tokenCount: undefined,
      sourceDocuments: []
    };
  }
};

export const parseRegularChatResponse = (receivedText: string) => {
  try {
    const startIndex = receivedText.indexOf('{"source_documents":');
    let resultText = receivedText;
    let sourceDocuments = [];
    let tokenCount;

    if (startIndex !== -1) {
      try {
        const jsonString = receivedText.slice(startIndex);
        const jsonData = JSON.parse(jsonString);
        const jsonLength = jsonString.length;

        resultText = receivedText.slice(0, startIndex) + receivedText.slice(startIndex + jsonLength);
        sourceDocuments = jsonData.source_documents || [];
        tokenCount = jsonData.token_count;
      } catch (error) {
        console.error("Error parsing source documents:", error);
      }
    }

    return {
      resultText,
      imageBase64: "",
      tokenCount,
      sourceDocuments
    };
  } catch (error) {
    console.error("Error processing stream completion:", error);
    return {
      resultText: receivedText,
      imageBase64: "",
      tokenCount: undefined,
      sourceDocuments: []
    };
  }
};

export const formatAdvancedSettings = (
  format: string,
  currentScope: string,
  selectedKnowledgeBase: string | null,
  chatDepartment: string,
  scoreThreshold: number,
  modelContext: string,
  conversationStyle: string,
  settings: any[],
  selectedModel: string
) => {
  if (format === "text") {
    let advancedSettingsText = "";
    advancedSettingsText += `This conversation was generated using the following advanced settings:\nScope: ${currentScope}\n`;

    if (selectedKnowledgeBase) {
      advancedSettingsText += `Department: ${chatDepartment}\nKnowledge Base: ${selectedKnowledgeBase}\nScore Threshold: ${scoreThreshold}\n`;
    }

    if (modelContext === "presets") {
      advancedSettingsText += `Conversation Style: ${conversationStyle}\n`;
    } else if (modelContext === "custom") {
      let modelSettingsText = "";
      let model = settings.find((m: any) => m.name === selectedModel);

      for (const setting of model.settings) {
        modelSettingsText += `${setting.label}: ${setting.defaultValue}\n`;
      }
      advancedSettingsText += `${modelSettingsText}`;
    }

    advancedSettingsText += "\n-----\n\n";

    return advancedSettingsText;
  } else {
    interface advancedSettingsObjectItem {
      [key: string]: any;
    }
    let advancedSettingsObject: advancedSettingsObjectItem = {};

    if (selectedKnowledgeBase) {
      advancedSettingsObject["Department"] = chatDepartment;
      advancedSettingsObject["Knowledgebase"] = selectedKnowledgeBase;
      advancedSettingsObject["Score Threshold"] = scoreThreshold;
    }

    if (modelContext === "presets") {
      advancedSettingsObject["Conversation Style"] = conversationStyle;
    } else if (modelContext === "custom") {
      let model = settings.find((m: any) => m.name === selectedModel);
      for (const setting of model.settings) {
        advancedSettingsObject[setting.label] = setting.defaultValue;
      }
    }

    advancedSettingsObject["Scope"] = currentScope;
    return advancedSettingsObject;
  }
};

export const exportChat = async (
  fileName: string,
  format: string,
  includePrompt: boolean,
  includeAdvancedSettings: boolean,
  chat: any[],
  advancedSettings?: any
) => {
  const accessToken = getAccessTokenDetails();
  const chatMessages = chat.map((item) => ({
    user: item.user,
    response: item.response?.data ?? item.response,
  }));
  
  const exportPayloadBody = {
    chat: chatMessages,
    documentType: format,
    fileName: fileName,
    includePrompt: includePrompt,
    ...(includeAdvancedSettings && { advancedSettings }),
  };

  const exportChatPayload = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(isLocalhost ? {} : { Authorization: bearer + accessToken?.accessToken }),
    },
    body: JSON.stringify(exportPayloadBody),
  };

  const response = await exportChatByFormat(exportChatPayload);
  if (!response.ok) {
    throw new Error("Failed to export chat");
  }
  return await response.blob();
};
